import { useEffect, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { operationalSources, observationLabel } from "@/lib/admin-operations";
import { adminSectionHref } from "@/lib/admin-navigation";

function useObservationClock() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

const severityLabel = { attention: "Attention", unknown: "Unknown", clear: "No attention reported" };
const severityStyle = {
  attention: "border-amber-400/50 bg-amber-400/10 text-amber-800 dark:text-amber-200",
  unknown: "border-slate-400/50 bg-slate-400/10 text-muted-foreground",
  clear: "border-emerald-400/50 bg-emerald-400/10 text-emerald-800 dark:text-emerald-200",
};

export function AdminInstanceContext() {
  const now = useObservationClock();
  const instance = useQuery<{
    authenticated: boolean;
    instance?: { runtimeMode: string; observedAt: string };
  }>({ queryKey: ["/api/admin/status"], refetchInterval: 60_000 });
  const paddle = useQuery<{ environment: string; checkedAt: string }>({
    queryKey: ["/api/admin/paddle/health"], refetchInterval: 60_000,
  });
  const instanceTime = Date.parse(instance.data?.instance?.observedAt ?? "");
  const paddleTime = Date.parse(paddle.data?.checkedAt ?? "");
  const fresh = (time: number) => Number.isFinite(time) && time <= now + 60_000 && now - time <= 180_000;
  const instanceKnown = !instance.isError && instance.data?.authenticated && fresh(instanceTime) &&
    ["development", "production"].includes(instance.data?.instance?.runtimeMode ?? "");
  const paddleKnown = !paddle.isError && fresh(paddleTime) &&
    ["sandbox", "live", "unconfigured"].includes(paddle.data?.environment ?? "");
  return (
    <div className="rounded-lg border bg-muted/30 p-3 text-xs space-y-1" aria-label="Running instance and billing environment">
      <p className="break-all"><strong>Running instance:</strong> {window.location.host} · Server mode: {instanceKnown ? instance.data?.instance?.runtimeMode : "unknown"}</p>
      <p><strong>Paddle environment:</strong> {paddleKnown ? paddle.data?.environment : "unknown"} · source checked {observationLabel(Number.isFinite(paddleTime) ? paddleTime : null)}</p>
      <p className="text-muted-foreground">Instance observed {observationLabel(Number.isFinite(instanceTime) ? instanceTime : null)}. Server mode and Paddle environment are separate. This describes this server, not another deployment.</p>
    </div>
  );
}

export default function AdminOperationsHome() {
  const now = useObservationClock();
  const queries = useQueries({
    queries: operationalSources.map(source => ({
      queryKey: [source.url],
      refetchInterval: source.interval,
      staleTime: source.interval,
    })),
  });
  const signals = operationalSources.map((source, index) => ({
    ...source, ...source.evaluate(queries[index], now),
  }));
  const attention = signals.filter(signal => signal.severity === "attention").length;
  const unknown = signals.filter(signal => signal.severity === "unknown").length;
  return (
    <div className="space-y-5" data-testid="admin-operations-home">
      <div>
        <h2 className="text-lg font-semibold">Needs attention</h2>
        <p className="text-sm text-muted-foreground" role="status">{attention} signals need review · {unknown} unknown. Each source is independent; this is not a delivery or launch guarantee.</p>
        <p className="mt-2 text-xs text-muted-foreground">Sources refresh every 30–60 seconds; Sentry every 5 minutes. Observations older than three refresh intervals are shown as unknown. “Received” is retrieval time, not a provider check time.</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {["Billing", "Notifications", "Application errors", "Quotas & delivery"].map(group => (
          <Card key={group}>
            <CardHeader className="pb-2"><CardTitle className="text-base">{group}</CardTitle></CardHeader>
            <CardContent className="divide-y">
              {signals.filter(signal => signal.group === group).sort((a, b) => {
                const order = { attention: 0, unknown: 1, clear: 2 };
                return order[a.severity] - order[b.severity];
              }).map(signal => (
                <article key={signal.id} className="py-3 space-y-1.5" data-testid={`signal-${signal.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link href={adminSectionHref(signal.destination)} className="font-medium text-sm underline underline-offset-4">{signal.label} →</Link>
                    <span className={`rounded border px-2 py-0.5 text-[11px] ${severityStyle[signal.severity]}`}>{severityLabel[signal.severity]}</span>
                  </div>
                  <p className="text-xs">{signal.detail}</p>
                  <p className="text-[11px] text-muted-foreground">{signal.timestampKind}: {observationLabel(signal.observedAt)}</p>
                </article>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Billing configuration checks do not establish Paddle acceptance or authorize release. Use Billing for readiness details, review queues and existing controls; System for advanced diagnostics and settings.</p>
    </div>
  );
}
